// Fixed categories for the application
export const CATEGORIES = [
  'Certification',
  'QDMS',
  'QHSE',
  'Forms and Checklists',
  'Procurement',
  'Maintenance',
  'General'
];

// Question-specific tags
export const QUESTION_TAGS = [
  'Workflow',
  'Access-Related',
  'Others'
];

// Learning-specific tags
export const LEARNING_TAGS = [
  'Best Practices',
  'Process Improvement',
  'Learning Tip'
];

// Legacy tags (for backward compatibility)
export const TAGS = [
  'Feature Request',
  'Bug/Issue',
  'Question',
  'Learning/Tip',
  'Miscellaneous'
];

// Feedback types
export const FEEDBACK_TYPES = [
  'Suggestion',
  'Bug',
  'Pain Point',
  'UI/UX',
  'Process',
  'Other'
];

// Feedback sources
export const FEEDBACK_SOURCES = [
  'Personal',
  'Ship'
];

// Domain configuration
export const ALLOWED_DOMAINS = ['maersk.com', 'maerskcrew.com', 'mariapps.com'];

export const DOMAIN_ROLES = {
  'maerskcrew.com': 'Super User',
  'mariapps.com': 'Mariapps SME',
  'maersk.com': 'Maersk Team'
};

// Role permissions (for future use)
export const ROLE_PERMISSIONS = {
  'Super User': {
    canModerateContent: true,
    canManageUsers: true,
    canAccessAllModules: true,
    canMarkAnswersAsCorrect: true
  },
  'Mariapps SME': {
    canModerateContent: true,
    canManageUsers: false,
    canAccessAllModules: true,
    canMarkAnswersAsCorrect: true
  },
  'Maersk Team': {
    canModerateContent: false,
    canManageUsers: false,
    canAccessAllModules: true,
    canMarkAnswersAsCorrect: false
  }
};

// Category color mapping
export const getCategoryColor = (category) => {
  const colors = {
    'Certification': 'bg-blue-100 text-blue-800',
    'QDMS': 'bg-green-100 text-green-800',
    'QHSE': 'bg-red-100 text-red-800',
    'Forms and Checklists': 'bg-purple-100 text-purple-800',
    'Procurement': 'bg-orange-100 text-orange-800',
    'Maintenance': 'bg-yellow-100 text-yellow-800',
    'General': 'bg-gray-100 text-gray-800'
  };
  return colors[category] || 'bg-gray-100 text-gray-800';
};

// Role color mapping
export const getRoleColor = (role) => {
  const colors = {
    'Super User': 'bg-purple-100 text-purple-800',
    'Maersk Team': 'bg-blue-100 text-blue-800',
    'Mariapps SME': 'bg-green-100 text-green-800'
  };
  return colors[role] || 'bg-gray-100 text-gray-800';
};

// Question tag color mapping
export const getQuestionTagColor = (tag) => {
  const colors = {
    'Workflow': 'bg-blue-100 text-blue-800',
    'Access-Related': 'bg-red-100 text-red-800',
    'Others': 'bg-gray-100 text-gray-800'
  };
  return colors[tag] || 'bg-gray-100 text-gray-800';
};

// Learning tag color mapping
export const getLearningTagColor = (tag) => {
  const colors = {
    'Best Practices': 'bg-green-100 text-green-800',
    'Process Improvement': 'bg-purple-100 text-purple-800',
    'Learning Tip': 'bg-blue-100 text-blue-800'
  };
  return colors[tag] || 'bg-gray-100 text-gray-800';
};

// Legacy tag color mapping (for backward compatibility)
export const getTagColor = (tag) => {
  const colors = {
    'Feature Request': 'bg-indigo-100 text-indigo-800',
    'Bug/Issue': 'bg-red-100 text-red-800',
    'Question': 'bg-blue-100 text-blue-800',
    'Learning/Tip': 'bg-green-100 text-green-800',
    'Miscellaneous': 'bg-gray-100 text-gray-800',
    // Question tags
    'Workflow': 'bg-blue-100 text-blue-800',
    'Access-Related': 'bg-red-100 text-red-800',
    'Others': 'bg-gray-100 text-gray-800',
    // Learning tags
    'Best Practices': 'bg-green-100 text-green-800',
    'Process Improvement': 'bg-purple-100 text-purple-800',
    'Learning Tip': 'bg-blue-100 text-blue-800'
  };
  return colors[tag] || 'bg-gray-100 text-gray-800';
};

// Feedback type color mapping
export const getFeedbackTypeColor = (feedbackType) => {
  const colors = {
    'Suggestion': 'bg-emerald-100 text-emerald-800',
    'Bug': 'bg-red-100 text-red-800',
    'Pain Point': 'bg-amber-100 text-amber-800',
    'UI/UX': 'bg-violet-100 text-violet-800',
    'Process': 'bg-cyan-100 text-cyan-800',
    'Other': 'bg-slate-100 text-slate-800'
  };
  return colors[feedbackType] || 'bg-gray-100 text-gray-800';
};

// Source color mapping
export const getSourceColor = (source) => {
  const colors = {
    'Personal': 'bg-teal-100 text-teal-800',
    'Ship': 'bg-indigo-100 text-indigo-800'
  };
  return colors[source] || 'bg-gray-100 text-gray-800';
};

// Status color mapping
export const getStatusColor = (status) => {
  switch (status) {
    case 'Open': return 'bg-blue-100 text-blue-800';
    case 'Answered': return 'bg-green-100 text-green-800';
    case 'In Progress': return 'bg-yellow-100 text-yellow-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

// Module navigation mapping
export const getModuleRoute = (category) => {
  const routes = {
    'Certification': 'module-certification',
    'QDMS': 'module-qdms',
    'QHSE': 'module-qhse',
    'Forms and Checklists': 'module-forms',
    'Procurement': 'module-procurement',
    'Maintenance': 'module-maintenance',
    'General': 'module-general'
  };
  return routes[category] || 'dashboard';
};

// Helper functions for role-based access
export const canUserModerateContent = (user) => {
  const role = user?.user_metadata?.user_role;
  return ROLE_PERMISSIONS[role]?.canModerateContent || false;
};

export const canUserManageUsers = (user) => {
  const role = user?.user_metadata?.user_role;
  return ROLE_PERMISSIONS[role]?.canManageUsers || false;
};

export const canUserMarkAnswersAsCorrect = (user) => {
  const role = user?.user_metadata?.user_role;
  return ROLE_PERMISSIONS[role]?.canMarkAnswersAsCorrect || false;
};

export const getUserRole = (user) => {
  return user?.user_metadata?.user_role || 'User';
};

export const getUserDomain = (user) => {
  return user?.email?.split('@')[1] || '';
};